/**
 * Coverage meter (Phase 65 / BLU-594 PR2). A thin scaleX bar — emerald when
 * complete; while partial the tone says WHY it is partial (DESIGN §1 keeps
 * amber for warnings, so work-in-flight uses the brand purple). Animates on
 * the transform per DESIGN §5 (0.2s ease-out), motion-reduce safe. Accessible
 * as a progressbar with a caller-supplied label; supports an indeterminate
 * state for "in flight, extent unknown".
 */
import { cn } from '@/lib/utils';

interface CoverageBarProps {
  /** 0-100. Ignored when `indeterminate`. */
  value: number;
  /** True when fully covered (emerald). */
  complete: boolean;
  label: string;
  /**
   * Tone while NOT complete:
   * - `attention` (default): amber — a coverage gap the operator should close
   *   (mapping / derivation surfaces).
   * - `progress`: brand purple — work in flight, nothing is wrong.
   */
  tone?: 'attention' | 'progress';
  /**
   * Extent not yet known (e.g. a run whose total has not loaded). Pulses the
   * whole track instead of asserting a false 0%, and drops `aria-valuenow` so
   * assistive tech announces "busy" rather than "0 percent".
   */
  indeterminate?: boolean;
  className?: string;
}

export function CoverageBar({ value, complete, label, tone = 'attention', indeterminate = false, className }: CoverageBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const partialFill = tone === 'progress' ? 'bg-threadheads-purple' : 'bg-amber-500';
  return (
    <span
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn('block h-1 w-full overflow-hidden rounded-full bg-muted', className)}
    >
      {indeterminate ? (
        <span
          aria-hidden
          className={cn('block h-full w-full rounded-full motion-safe:animate-pulse', tone === 'progress' ? 'bg-threadheads-purple/30' : 'bg-amber-500/30')}
        />
      ) : (
        <span
          aria-hidden
          className={cn(
            'block h-full origin-left rounded-full transition-transform duration-200 ease-out motion-reduce:transition-none',
            complete ? 'bg-emerald-500' : partialFill,
          )}
          style={{ transform: `scaleX(${clamped / 100})` }}
        />
      )}
    </span>
  );
}
